/**
 * Fonte única de verdade do mapeamento da ação de um registo
 * (AtividadeLog.AcaoLogs) para ícone + cor.
 *
 * É usada pela coluna "Ação" da tabela e pelo card "Ação Executada" do drawer
 * "Detalhe do Registo", para que texto, ícone e cor representem sempre a mesma
 * ação.
 */

export type AcaoLogsChave = 'criar' | 'atualizar' | 'eliminar' | 'neutro';

export interface AcaoVisual {
  /** Classe de badge (define ícone + cor). */
  badge: string;
  /** Classe do ícone (remixicon). */
  icon: string;
}

const VISUAL_POR_ACAO: Record<AcaoLogsChave, AcaoVisual> = {
  criar: { badge: 'badge-success', icon: 'ri-add-circle-line' },
  atualizar: { badge: 'badge-info', icon: 'ri-edit-line' },
  eliminar: { badge: 'badge-danger', icon: 'ri-delete-bin-line' },
  neutro: { badge: 'badge-secondary', icon: 'ri-information-line' },
};

/**
 * Palavras-chave reconhecidas no rótulo devolvido pela API
 * (`AtividadeLog.AcaoLogs`). A ordem é relevante: "exclusão" só pode cair em
 * "eliminar".
 */
const PALAVRAS_CHAVE: ReadonlyArray<
  readonly [AcaoLogsChave, readonly string[]]
> = [
  [
    'eliminar',
    [
      'eliminar',
      'eliminacao',
      'excluir',
      'exclusao',
      'remover',
      'remocao',
      'apagar',
      'delete',
    ],
  ],
  [
    'atualizar',
    [
      'atualizar',
      'atualizacao',
      'editar',
      'edicao',
      'alterar',
      'alteracao',
      'modificar',
      'update',
    ],
  ],
  [
    'criar',
    [
      'criar',
      'criacao',
      'create',
      'adicionar',
      'adicao',
      'inserir',
    ],
  ],
];

/**
 * Fallback numérico, apenas usado quando a API não devolve o rótulo.
 * Estes valores correspondem ao fallback de `acaoOptions`
 * (1 = Criar, 2 = Editar, 3 = Eliminar, 4 = Visualizar, 5 = Login, 6 = Logout)
 * e são substituídos em runtime pelo enum `GET /api/Enum/AcaoLogs`.
 */
const ACAO_POR_ENUM: Record<number, AcaoLogsChave> = {
  1: 'criar',
  2: 'atualizar',
  3: 'eliminar',
};

function normalizar(texto: string | null | undefined): string {
  return (texto ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

/**
 * Resolve o visual da ação. O rótulo textual tem prioridade sobre o valor
 * numérico do enum, porque é o rótulo que é apresentado ao utilizador: assim
 * ícone e cor nunca discordam do texto exibido.
 */
export function resolverVisualAcao(
  acaoEnum?: number | null,
  acaoLabel?: string | null,
): AcaoVisual {
  const texto = normalizar(acaoLabel);

  if (texto) {
    for (const [chave, palavras] of PALAVRAS_CHAVE) {
      if (palavras.some((palavra) => texto.includes(palavra))) {
        return VISUAL_POR_ACAO[chave];
      }
    }
  }

  const chaveEnum =
    acaoEnum === null || acaoEnum === undefined
      ? undefined
      : ACAO_POR_ENUM[acaoEnum];

  return VISUAL_POR_ACAO[chaveEnum ?? 'neutro'];
}
